import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { buttonClass } from '@/lib/button'
import SectionHeading from '@/components/landing/section-heading'

import {
    ArrowUpRight,
    Mail,
    MapPin,
    Navigation,
    Phone
} from 'lucide-react'

import {
    centers,
    contactEmail,
    contactPhones,
    socialLinks
} from '@/constants/content'

const contactRowClass = 'flex items-center justify-between gap-3 rounded-lg bg-white/10 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal'

const CentersSection: FC = (): ReactNode => {
    return (
        <section id='centers' aria-labelledby='centers-title' className='scroll-mt-20 py-20 sm:py-24 lg:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-8'>
                <SectionHeading
                    id='centers-title'
                    eyebrow="Training centers"
                    title="Train in person in Hyderabad or Vijayawada."
                    description="Pick the center you want to attend when you register. It's also the center you choose when you sign in to the portal."
                />

                <div className='mt-12 grid gap-5 lg:grid-cols-3'>
                    {centers.map((center, index) => (
                        <article key={center.id} className='flex flex-col overflow-hidden rounded-2xl border border-border bg-card'>
                            <div className={cn('relative h-36 overflow-hidden', index === 0 ? 'bg-secondary' : 'bg-teal/15')}>
                                <div aria-hidden='true' className='dot-grid absolute inset-0 opacity-70' />
                                <div aria-hidden='true' className='absolute -right-10 -top-12 size-44 rounded-full bg-primary/15 blur-2xl' />

                                <p aria-hidden='true' className='absolute -bottom-3 left-5 whitespace-nowrap font-heading text-6xl font-bold tracking-tight text-primary/10'>
                                    {center.city}
                                </p>

                                <span className='absolute left-6 top-6 flex size-11 items-center justify-center rounded-xl bg-white text-primary shadow-sm'>
                                    <MapPin className='size-5' aria-hidden='true' />
                                </span>
                            </div>

                            <div className='flex flex-1 flex-col p-6 sm:p-7'>
                                <p className='font-mono text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground'>
                                    {center.area}
                                </p>

                                <h3 className='mt-2 text-2xl font-semibold text-foreground'>
                                    {center.city}
                                </h3>

                                <address className='mt-4 flex-1 text-[15px] not-italic leading-relaxed text-muted-foreground'>
                                    {center.addressLines.map((line) => (
                                        <span key={line} className='block'>
                                            {line}
                                        </span>
                                    ))}
                                </address>

                                <a
                                    href={center.mapUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={buttonClass('outline', 'md', 'mt-6 self-start')}
                                >
                                    <Navigation className='size-4' aria-hidden='true' />
                                    Get directions
                                    <span className='sr-only'>to the {center.city} center (opens in a new tab)</span>
                                </a>
                            </div>
                        </article>
                    ))}

                    <article id='contact' aria-labelledby='contact-title' className='relative flex scroll-mt-24 flex-col overflow-hidden rounded-2xl bg-linear-to-br from-primary to-ink p-6 text-white sm:p-7'>
                        <div aria-hidden='true' className='absolute -right-16 -top-16 size-52 rounded-full bg-teal/25 blur-3xl' />

                        <p className='relative font-mono text-xs font-medium uppercase tracking-[0.18em] text-white/60'>
                            Contact us
                        </p>

                        <h3 id='contact-title' className='relative mt-2 text-2xl font-semibold'>
                            Talk to our team
                        </h3>

                        <p className='relative mt-2 text-[15px] leading-relaxed text-white/70'>
                            Questions about tracks, batches or fees? Call us or send an email.
                        </p>

                        <ul className='relative mt-6 space-y-2'>
                            {contactPhones.map((phone) => (
                                <li key={phone.href}>
                                    <a href={phone.href} className={contactRowClass}>
                                        <span className='flex items-center gap-3'>
                                            <Phone className='size-4 text-teal' aria-hidden='true' />
                                            {phone.display}
                                        </span>

                                        <span className='text-xs font-medium text-white/60'>
                                            Call
                                        </span>
                                    </a>
                                </li>
                            ))}

                            <li>
                                <a href={`mailto:${contactEmail}`} className={contactRowClass}>
                                    <span className='flex min-w-0 items-center gap-3'>
                                        <Mail className='size-4 shrink-0 text-teal' aria-hidden='true' />
                                        <span className='truncate'>{contactEmail}</span>
                                    </span>

                                    <span className='text-xs font-medium text-white/60'>
                                        Email
                                    </span>
                                </a>
                            </li>
                        </ul>

                        <div className='relative mt-auto pt-8'>
                            <p className='text-xs font-medium text-white/60'>
                                Follow Webify
                            </p>

                            <ul className='mt-3 flex flex-wrap gap-2'>
                                {socialLinks.map((social) => (
                                    <li key={social.label}>
                                        <a
                                            href={social.href}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='inline-flex h-9 items-center gap-1.5 rounded-full border border-white/15 px-3 text-xs font-semibold text-white/85 transition-colors hover:border-white/30 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal'
                                        >
                                            <social.icon className='size-3.5' aria-hidden='true' />
                                            {social.label}
                                            <ArrowUpRight className='size-3 text-white/50' aria-hidden='true' />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </article>
                </div>
            </div>
        </section>
    )
}

export default CentersSection
